import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { utils, read } from "xlsx";
import Editor from "@monaco-editor/react";
import { useTheme } from "@mui/material/styles";
import DTCFileComponent from './DTCFileComponent';
import PNPComponent from "./PNPComponent";
import PCAPFileViewer from "./PCAPFileViewer";
import XMLFileViewer from "./XMLFileViewer";
import Frame from "react-frame-component";
import dayjs from "dayjs";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import "./FilePreview.css";

function ThemedEditor({ height = "400px", language, value, options }) {
  const theme = useTheme();
  return (
    <Editor
      height={height}
      language={language}
      value={value}
      options={options}
      theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'vs-light'}
    />
  );
}

export default function FilePreview({ file, blobUrl, onRefreshUrl, onError }) {
  const [content, setContent] = useState("Loading...");
  const navigate = useNavigate();
  const ext = useMemo(() => file?.file_type?.toLowerCase() || "", [file]);
  
  const fetchedUrlRef = useRef(null);
  const isFetchingRef = useRef(false);
  
  const displayFilename = useMemo(() => {
    if (!file?.filename) return "Unknown File";
    const filename = file.filename;
    const lastSlash = Math.max(filename.lastIndexOf('/'), filename.lastIndexOf('\\'));
    return lastSlash >= 0 ? filename.substring(lastSlash + 1) : filename;
  }, [file?.filename]);

  const isDTCFile = file?.category === "dtc-read";
  const isPNPFile = ["pn-pull", "pn-pull-common", "pn-pull-ipa"].includes(file?.category);
  const isEventFile = file?.category === "event";
  const isTestOutputFile = ["test-output", "vsrt-report"].includes(file?.category);

  const buildLastWeekParams = (useSnakeCase = true) => {
    const endDate = dayjs().endOf('day');
    const startDate = dayjs().subtract(7, 'day').startOf('day');
    
    const params = new URLSearchParams();
    
    if (useSnakeCase) {
      params.set('start_date', startDate.format());
      params.set('end_date', endDate.format());
    } else {
      params.set('startDate', startDate.format());
      params.set('endDate', endDate.format());
    }
    
    if (file?.vin) {
      params.set('vin', file.vin);
    }
    
    return params.toString();
  };

  const handleViewLastWeekDTCLogs = () => {
    navigate(`/dtc_log?${buildLastWeekParams(true)}`);
  };

  const handleViewLastWeekPNP = () => {
    navigate(`/part_number_pull?${buildLastWeekParams(false)}`);
  };

  const handleViewLastWeekEvents = () => {
    navigate(`/event?${buildLastWeekParams(false)}`);
  };

  const handleViewLastWeekTestResults = () => {
    navigate(`/test_result_ingestion?${buildLastWeekParams(false)}`);
  };

  const triggerDownload = useCallback((url) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = displayFilename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [displayFilename]);

  const handleDownload = useCallback(async () => {
    if (!blobUrl) return;
    try {
      // Always get a fresh URL if possible, in case the SAS token expired
      if (onRefreshUrl) {
        try {
          const freshUrl = await onRefreshUrl();
          triggerDownload(freshUrl);
          return;
        } catch (refreshErr) {
          console.error('Failed to refresh URL, using existing:', refreshErr);
        }
      }
      // Fallback to current blobUrl
      triggerDownload(blobUrl);
    } catch (err) {
      console.error('Download error:', err);
      onError?.('Failed to download file. Please try again.');
    }
  }, [blobUrl, triggerDownload, onRefreshUrl, onError]);

  const customComponentCategories = ["dtc-read", "pn-pull", "pn-pull-common", "pn-pull-ipa"];
  const needsCustomComponentByCategory = customComponentCategories.includes(file?.category);

  const customComponentFileTypes = [".xml", ".pcap"];
  const needsCustomComponentByFileType = customComponentFileTypes.includes(ext);

  const needsCustomComponent = needsCustomComponentByCategory || needsCustomComponentByFileType;

  const textFileExtensions = [".txt", ".json", ".log", ".csv", ".xml"];
  const isTextFile = textFileExtensions.includes(ext);

  useEffect(() => {
    const loadContent = async () => {
      if (!file?.filename) {
        const message = "Invalid file";
        setContent(<div>{message}</div>);
        onError?.(message);
        return;
      }

      if (!blobUrl) {
        const message = "Invalid file URL";
        setContent(<div>{message}</div>);
        onError?.(message);
        return;
      }

      if (fetchedUrlRef.current === blobUrl || isFetchingRef.current) {
        return;
      }

      try {
        if (needsCustomComponent) {
          setContent("");
          fetchedUrlRef.current = blobUrl;
          return;
        }

        if ([".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp"].includes(ext)) {
          setContent(<img src={blobUrl} alt={file.filename} style={{ maxWidth: "100%" }} />);
          fetchedUrlRef.current = blobUrl;
        } else if ([".mp3", ".wav"].includes(ext)) {
          setContent(<audio controls src={blobUrl} />);
          fetchedUrlRef.current = blobUrl;
        } else if ([".mp4", ".webm", ".ogg"].includes(ext)) {
          setContent(
            <video controls style={{ maxWidth: "100%" }}>
              <source src={blobUrl} type={`video/${ext.slice(1)}`} />
              Your browser does not support the video tag.
            </video>
          );
          fetchedUrlRef.current = blobUrl;
        } else if (ext === ".pdf") {
          setContent(<embed src={blobUrl} type="application/pdf" width="100%" height="800px" />);
          fetchedUrlRef.current = blobUrl;
        }
        else if (ext === ".html") {
          isFetchingRef.current = true;
          const htmlText = await fetch(blobUrl).then((res) => {
            if (!res.ok) throw new Error("Failed to fetch HTML");
            return res.text();
          });

          const script = `
            <script>
              document.addEventListener('click', function(e) {
                if (e.target.tagName === 'A') {
                  e.preventDefault();
                  console.log('Link click prevented. Href:', e.target.href);
                }
              }, true);
            </script>
          `;

          setContent(
            <Frame
              style={{ width: "100%", height: "800px", border: "none" }}
              initialContent={`<!DOCTYPE html><html><head><base href="${blobUrl}"></base></head><body>${htmlText}${script}</body></html>`}
            />
          );
          fetchedUrlRef.current = blobUrl;
          isFetchingRef.current = false;
        } else if (isTextFile) {
          isFetchingRef.current = true;
          const text = await fetch(blobUrl).then((res) => {
            if (!res.ok) throw new Error("Failed to fetch text file");
            return res.text();
          });
          const editorLanguage = ext === ".json" ? "json" : ext === ".xml" ? "xml" : ext === ".csv" ? "plaintext" : "plaintext";
          setContent(
            <ThemedEditor
              height="400px"
              language={editorLanguage}
              value={text}
              options={{
                readOnly: true,
                wordWrap: "on",
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 14,
                lineNumbers: "on",
              }}
            />
          );
          fetchedUrlRef.current = blobUrl;
          isFetchingRef.current = false;
        } else if (ext === ".xlsx" || ext === ".xls") {
          isFetchingRef.current = true;
          const buffer = await fetch(blobUrl).then((res) => {
            if (!res.ok) throw new Error("Failed to fetch Excel file");
            return res.arrayBuffer();
          });
          const workbook = read(buffer, { type: "array" });
          const html = utils.sheet_to_html(workbook.Sheets[workbook.SheetNames[0]]);
          setContent(<div dangerouslySetInnerHTML={{ __html: html }} />);
          fetchedUrlRef.current = blobUrl;
          isFetchingRef.current = false;
        } else if ([".gz", ".zip", ".rar", ".7z", ".tar", ".bz2"].includes(ext)) {
          const message = `This compressed file type (${ext}) cannot be previewed.`;
          setContent(<div>{message}</div>);
          fetchedUrlRef.current = blobUrl;
        } else {
          const message = `This file type (${ext}) cannot be previewed.`;
          setContent(<div>{message}</div>);
          fetchedUrlRef.current = blobUrl;
        }
      } catch (error) {
        isFetchingRef.current = false;
        const message = "Error loading file preview. Please try again with vpn or gm networking or try using Microsoft Edge browser.";
        setContent(<div>{message}</div>);
        onError?.(message);
      }
    };

    loadContent();
  }, [blobUrl, ext, file, onError, needsCustomComponent, isTextFile]);

  const renderCustomComponent = () => {
    if (needsCustomComponentByCategory) {
      switch (file?.category) {
        case "dtc-read":
          return (
            <DTCFileComponent 
              fileLocation={file.file_location} 
              filename={file.filename}
            />
          );
        case "pn-pull":
        case "pn-pull-common":
        case "pn-pull-ipa":
          return (
            <PNPComponent 
              fileLocation={file.file_location} 
              filename={file.filename} 
            />
          );
        default:
          return <div>Unknown custom component for category: {file?.category}</div>;
      }
    }

    if (needsCustomComponentByFileType) {
      switch (ext) {
        case ".pcap":
          return (
            <PCAPFileViewer
              blobUrl={blobUrl}
              filename={file.filename}
            />
          );
        case ".xml":
          return (
            <XMLFileViewer 
              fileLocation={file.file_location}
              filename={file.filename}
            />
          );
        default:
          return <div>Unknown custom component for file type: {ext}</div>;
      }
    }

    return <div>No custom component found</div>;
  };

  return (
    <div className="file-preview-container">
      <div className="file-preview-header">
        <div className="file-preview-header-row">
          <div className="file-preview-info">
            {file?.category && (
              <div className="file-preview-category">
                {file.category}
              </div>
            )}
            <h1 className="file-preview-filename">
              {displayFilename}
            </h1>
          </div>
          <div className="file-preview-actions">
            {isDTCFile && (
              <button className="file-preview-btn" onClick={handleViewLastWeekDTCLogs}>
                <OpenInNewIcon fontSize="small" />
                View Last Week DTC Logs
              </button>
            )}
            {isPNPFile && (
              <button className="file-preview-btn" onClick={handleViewLastWeekPNP}>
                <OpenInNewIcon fontSize="small" />
                View Last Week Part Number Pulls
              </button>
            )}
            {isEventFile && (
              <button className="file-preview-btn" onClick={handleViewLastWeekEvents}>
                <OpenInNewIcon fontSize="small" />
                View Last Week Events
              </button>
            )}
            {isTestOutputFile && (
              <button className="file-preview-btn" onClick={handleViewLastWeekTestResults}>
                <OpenInNewIcon fontSize="small" />
                View Last Week Test Results
              </button>
            )}
            {blobUrl && (
              <button
                className="file-preview-btn"
                onClick={handleDownload}
              >
                <DownloadIcon fontSize="small" />
                Download
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="file-preview-content">
        <div>
          {needsCustomComponent ? renderCustomComponent() : content}
        </div>
      </div>
    </div>
  );
}